import { Options, QueryKey } from '@crm/api'
import { getColor } from '@crm/components/Charts/utils'
import { useStyles } from '@crm/features/reports/styles'
import { abbreviateNumber } from '@crm/utils'
import { TDataShape } from '@hey-api/client-axios'
import { useLocales } from '@shared/locales'
import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import { Card, Col, Flex, List, Row, Typography } from 'antd'
import { useCallback, useMemo, useState } from 'react'
import {
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Sector,
  Tooltip
} from 'recharts'
import { PieSectorDataItem } from 'recharts/types/polar/Pie'

const { Title, Text } = Typography

type Props<
  TData extends TDataShape,
  TRow extends { name: string; value: number }
> = {
  label: string
  subLabel: string
  getQueryFunction: (
    options?: TData
  ) => UseQueryOptions<
    { data: TRow[]; total: number },
    Error,
    { data: TRow[]; total: number },
    QueryKey<Options<TData>>
  >
  baseOptions?: TData
}

export const ShareChart = <
  TData extends TDataShape,
  TRow extends { name: string; value: number }
>({
  label,
  subLabel,
  getQueryFunction,
  baseOptions
}: Props<TData, TRow>) => {
  const { t } = useLocales()
  const { styles } = useStyles()

  const [activeIndex, setActiveIndex] = useState<number | undefined>()

  const mergedOptions = useMemo<TData>(
    () => baseOptions ?? ({} as TData),
    [baseOptions]
  )

  const { data, isLoading } = useQuery(getQueryFunction(mergedOptions))

  const normalized = useMemo<{ data: TRow[]; total: number }>(() => {
    if (Array.isArray(data)) {
      return { data, total: 0 }
    }
    return data ?? { data: [], total: 0 }
  }, [data])

  const renderActiveShape = useCallback((props: PieSectorDataItem) => {
    const { cx, cy, innerRadius, outerRadius, startAngle, endAngle } = props

    return (
      <g>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={getColor(8, 'primary')}
        />
      </g>
    )
  }, [])

  return (
    <Card title={label} loading={isLoading} className="h-full">
      <Row gutter={16}>
        <Col span={12}>
          <Flex
            justify="center"
            align="center"
            vertical
            className={styles.chartTitle}
          >
            <Title level={3}>{abbreviateNumber(normalized.total || 0)}</Title>
            {subLabel}
          </Flex>

          <ResponsiveContainer width="100%" height="100%" minHeight={300}>
            <PieChart>
              <Tooltip
                content={({ active, payload }) =>
                  active && payload
                    ? `${payload[0].name} - ${(
                        payload[0].value as number
                      ).toFixed(2)} %`
                    : null
                }
              />
              <Pie
                activeIndex={activeIndex}
                activeShape={renderActiveShape}
                data={normalized.data}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                strokeWidth={0}
                dataKey="value"
                onMouseEnter={(_, index) => setActiveIndex(index)}
                onMouseLeave={() => setActiveIndex(undefined)}
              >
                {normalized?.data?.map((item, index: number) => (
                  <Cell
                    key={`cell-${(item as { name?: string }).name ?? index}`}
                    fill={getColor(index, 'primary')}
                  />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        </Col>

        <Col span={12}>
          <List
            className={styles.chartList}
            header={
              <Flex justify="space-between">
                <Text strong>{t('nomenclature.name')}</Text>
                <Text strong>{t('nomenclature.weight')}</Text>
              </Flex>
            }
            bordered={false}
            dataSource={
              normalized.data as Array<{ name: string; value: number }>
            }
            renderItem={(item, index) => (
              <List.Item>
                <List.Item.Meta
                  title={item?.name}
                  avatar={
                    <div
                      className="reports-point"
                      style={{ background: getColor(index, 'primary') }}
                    />
                  }
                />
                <Text strong>{item?.value?.toFixed(2)} %</Text>
              </List.Item>
            )}
          />
        </Col>
      </Row>
    </Card>
  )
}
